import type { WorkflowDefinition } from "../types";

/**
 * W26 — Write the answers to the questions AI is already asking.
 *
 * `shared`: the gap list belongs to the property, and a content team works one
 * list together. `explicit`, because a gap review is a piece of work with a
 * start date: a site that closed its gaps last spring has not started this one.
 *
 * The closing step is the exception the whole playbook turns on — see the
 * comment on it.
 */
export const contentGapsWorkflow: WorkflowDefinition = {
	id: "content-gaps",
	wNumber: 26,
	title: "Find content gaps from the questions people really ask",
	outcome:
		"Turn the questions AI already asks into pages, then check whether the gap closed.",
	audiences: ["content", "inhouse"],
	uses: [
		"AI Visibility Rankings",
		"FAQ Generator",
		"Property Pages",
		"Projects",
	],
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	// The FAQ Generator answers questions from a page it can crawl, which is a
	// BRAND's own site. PRODUCT and PERSON have no site of their own.
	isApplicable: (mode) => mode.showContent,
	articleSlug: "how-to-find-content-gaps-from-real-ai-questions",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "find-gaps",
			title: "Find the questions you do not answer",
			page: "AI Visibility Rankings",
			route: "/ai-visibility-rankings",
			criterion: "You've picked the gaps worth answering.",
			why: "AI writes down the searches it makes before it answers. Reading those beats guessing at keywords.",
			docsHref: "/docs/dashboards/ai-visibility-rankings",
			tasks: [
				"Filter for the searches where you have no rank",
				"Mark each one answered well, answered badly, or not answered",
				"Sort what is left by how close the buyer is to choosing",
			],
			enrichment: "coverageScore",
			// Judgement call: nothing watches "you decided what to write".
			complete: { kind: "manual" },
		},
		{
			id: "generate-faqs",
			title: "Generate answers from the real questions",
			page: "FAQ Generator",
			route: "/faq-generator",
			criterion: "You've generated answers since you started.",
			why: "The generator sources questions from People Also Ask, Reddit and Quora. It answers them only from what the page already says.",
			docsHref: "/docs/dashboards/faq-generator",
			tasks: [
				"Point it at the page that should answer the question",
				"Drop the questions that do not belong on that page",
				"Export the visible block and the structured data",
			],
			complete: { kind: "since", event: "faqGenerated" },
			spend: { kind: "faqRun" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Question sets generated",
							value: state.property.faqGenerationCount,
							target: 1,
						}
					: null,
		},
		{
			id: "publish-answers",
			title: "Publish the answers where people ask them",
			page: "Property Pages",
			route: "/pages",
			criterion: "You've published the answers.",
			why: "An answer has to make sense once it is lifted out of the page. Put it under a heading in the reader's own words.",
			docsHref: "/docs/dashboards/property-pages",
			tasks: [
				"Answer in the first two sentences, then explain",
				"Use numbers and plain nouns, because those get quoted",
				"Add a few tight questions per page, never a hundred",
			],
			complete: { kind: "manual" },
		},
		{
			id: "measure",
			title: "Measure whether the gap closed",
			page: "Projects",
			route: "/projects",
			criterion: "You've got a project asking those questions daily.",
			why: "The same questions have to be asked again for the comparison to mean anything. Two to six weeks is normal before anything moves.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Keep the original question list and add to the bottom",
				"Track the questions on a project so they are asked daily",
				"Watch the page reach the results first, then the citations",
			],
			// An existing project is the right home for the new pages, so this
			// one step is allowed to start ticked. A second project would split
			// the comparison the playbook exists to make.
			complete: { kind: "count", key: "activeProjectCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
	],
};
